
import { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { CheckCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNodeSettings } from '../NodeSettingsContext';
import { type StateNodeData } from '@/types/workflow-graph';
import { cn } from '@/lib/utils';

interface SucceedNodeProps {
    data: StateNodeData;
    selected?: boolean;
}

export const SucceedNode = memo(function SucceedNode({ data, selected }: SucceedNodeProps) {
    const { openSettings } = useNodeSettings();
    const { layoutDirection = 'LR' } = data;
    
    // Determine handle position based on layout direction
    const isHorizontal = layoutDirection === 'LR';
    const inputPosition = isHorizontal ? Position.Left : Position.Top;
    
    return (
        <div className="relative w-fit">
            {/* Settings button - positioned outside the circle */}
            <div className="absolute -right-5 -top-1 z-10">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 rounded-full bg-white text-slate-400 shadow-sm border border-slate-200 hover:bg-slate-100 hover:text-slate-600"
                    title="Node Settings"
                    onClick={(e) => {
                        e.stopPropagation();
                        openSettings(data);
                    }}
                >
                    <Settings size={10} />
                </Button>
            </div>

            <div
                className={cn(
                    'flex h-20 w-20 items-center justify-center rounded-full border-2 border-green-300 bg-green-50',
                    selected && 'ring-2 ring-blue-500 ring-offset-2'
                )}
            >
                {/* Input Handle - position based on layout direction */}
                <Handle
                    type="target"
                    id="target"
                    position={inputPosition}
                    className="!h-3 !w-3 !border-green-500 !bg-green-400"
                />
                <div className="text-center">
                    <CheckCircle className="mx-auto h-6 w-6 text-green-600" />
                    <span className="mt-1 block text-xs font-medium text-green-700">{data.label}</span>
                </div>
            </div>
        </div>
    );
});
